import React, { useEffect, useMemo } from 'react';

import { FocusContext, useFocusable } from '@noriginmedia/norigin-spatial-navigation';

import Button from '@/components/Button';
import CustomTypography from '@/components/Typography/CustomTypography';

import { Language } from '@/services/graphQL/api/generated/types_web';
import { Keys, getSystem } from '@/system';
import { getLocalization, stringFromFormat } from '@/utils/localization';

import { useAccessibility } from '../Accessibility/AccessibilityContext';
import ModalClose from './ModalClose';

type ModalProps = {
  children?: React.ReactNode;
  title?: string;
  description?: string;
  saveButtonStyleClasses?: string;
  onSave: () => void;
  saveText?: string;
  onCancel?: () => void;
  cancelText?: string;
  showCloseButton?: boolean;
  closeModal: () => void;
  styleClasses?: string;
  userLanguage?: Language;
  variant?: 'destructive' | 'default';
  defaultFocusKey?: string;
  restoreFocus?: boolean;
};

const Modal: React.FC<ModalProps> = ({
  children,
  title,
  description,
  saveButtonStyleClasses,
  saveText,
  onSave,
  onCancel,
  cancelText,
  showCloseButton = false,
  closeModal,
  styleClasses,
  userLanguage,
  variant = 'default',
  defaultFocusKey = 'cancel',
  restoreFocus = false,
}) => {
  const genericLocale = getLocalization(userLanguage)?.['generic'];

  const { ref, focusKey, focusSelf } = useFocusable({
    isFocusBoundary: true,
    focusKey: 'modal',
    focusBoundaryDirections: ['up', 'down', 'left', 'right'],
    preferredChildFocusKey: defaultFocusKey,
  });
  const system = getSystem();
  const { announceToScreenReader } = useAccessibility();

  useEffect(() => {
    focusSelf();
    const pageCopy = `${title || ''}, ${description || ''} ${onCancel ? `${cancelText}, ${genericLocale?.['button']}` : `${saveText}, ${genericLocale?.['button']}`} `;
    announceToScreenReader(pageCopy, 'assertive');
  }, []);

  useEffect(() => {
    if (restoreFocus) focusSelf();
  }, [restoreFocus]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const key = system.key(event);
      if (key === Keys.BACK) {
        closeModal();
        event.stopImmediatePropagation();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, []);

  const cancelButtonAriaLabel = useMemo(() => {
    return `${cancelText}, ${genericLocale?.['button']}, ${stringFromFormat(getLocalization(userLanguage)['voice-over']['list-x-of-y'], 1, 2)}`;
  }, [cancelText, genericLocale, userLanguage]);

  const saveButtonAriaLabel = useMemo(() => {
    return `${saveText}, ${genericLocale?.['button']}, ${onCancel ? stringFromFormat(getLocalization(userLanguage)['voice-over']['list-x-of-y'], 2, 2) : ''}`;
  }, [saveText, genericLocale, userLanguage, onCancel]);

  return (
    <FocusContext.Provider value={focusKey}>
      <div
        className="fixed w-full h-full z-50 top-0 left-0 bottom-0 flex items-center justify-center bg-primitive-semantic-grey-100/70"
        ref={ref}
      >
        <div
          className={`bg-primitive-semantic-grey-50 pb-8 px-[2rem] py-[2rem] rounded-2xl text-center min-w-[44.125rem] ${styleClasses}`}
        >
          {showCloseButton && (
            <div className={`text-right`}>
              <ModalClose
                onClick={closeModal}
                focusKey="modal-close-button"
                text={getLocalization(userLanguage)['generic']['close'] || 'Close modal'}
              />
            </div>
          )}
          <div>
            {children ? (
              children
            ) : (
              <>
                <div className="mb-[2.5rem]">
                  <CustomTypography variant="10ftTitle2">{title}</CustomTypography>
                  {description && (
                    <CustomTypography
                      variant="10ftBody"
                      styleClasses={`mt-[1.5rem] whitespace-break-spaces`}
                    >
                      {description}
                    </CustomTypography>
                  )}
                </div>
                <div role="list" className="flex items-center justify-center">
                  {onCancel && (
                    <Button
                      role="listitem"
                      className="mx-4 min-w-[15.875rem] whitespace-break-spaces"
                      size="auto"
                      onClick={onCancel}
                      aria-label={cancelButtonAriaLabel}
                      focusKey="cancel"
                    >
                      {cancelText}
                    </Button>
                  )}
                  <Button
                    className={`mx-4 min-w-[15.875rem] ${variant === 'destructive' ? 'bg-primitive-semantic-red-10/20 text-buttons-primary-color' : ''} ${saveButtonStyleClasses}`}
                    focusedStyleClasses={
                      variant === 'destructive' ? 'bg-status-high-alert text-white' : ''
                    }
                    size="auto"
                    onClick={onSave}
                    aria-label={saveButtonAriaLabel}
                    focusKey="save"
                  >
                    {saveText}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </FocusContext.Provider>
  );
};

export default Modal;
